import { RRule, SPANISH } from 'rrule'
import dayjs from '../../../utils/dayjsConfig'

export const POS_SCHEDULE_UI_TYPES = Object.freeze({
  DAILY: 'daily',
  WEEKLY: 'weekly',
  ONCE: 'once',
  ONCE_PER_PERIOD: 'once_per_period'
})

export const ONE_TIME_RECURRENCE_TYPES = [
  POS_SCHEDULE_UI_TYPES.ONCE,
  POS_SCHEDULE_UI_TYPES.ONCE_PER_PERIOD
]

export const isOneTimeRecurrence = (recurrenceType) =>
  ONE_TIME_RECURRENCE_TYPES.includes(recurrenceType)

export const isSchedulePayloadValid = (payload) => {
  if (!payload?.completion_mode) return false

  if (payload.completion_mode === POS_SCHEDULE_UI_TYPES.ONCE) {
    return true
  }

  return Boolean(payload.recurrence_format)
}

export const TASK_SCHEDULE_INCOMPLETE_MESSAGE =
  'Por favor, complete la configuración de fechas y recurrencia antes de guardar.'

export const isTaskScheduleReady = ({ startDate, schedulePayload }) =>
  Boolean(startDate) && isSchedulePayloadValid(schedulePayload)

export const isTaskFormSubmittable = ({ startDate, schedulePayload, isBaseComplete }) =>
  Boolean(isBaseComplete) && isTaskScheduleReady({ startDate, schedulePayload })

export const getTaskSchedulePayloadForCollect = ({ startDate, schedulePayload }) => {
  if (!isTaskScheduleReady({ startDate, schedulePayload })) {
    return { ok: false, message: TASK_SCHEDULE_INCOMPLETE_MESSAGE }
  }

  return { ok: true, payload: schedulePayload }
}

export const getOneTimeRecurringDescription = (weeks = 1) => {
  const interval = Number(weeks) || 1
  return interval === 1 ? 'Una vez cada semana' : `Una vez cada ${interval} semanas`
}

const getWeekIntervalLabel = (weeks = 1) => {
  const interval = Number(weeks) || 1
  return interval === 1 ? 'cada semana' : `cada ${interval} semanas`
}

export const getRecurrenceDescription = ({
  recurrenceUiType,
  recurrenceInterval,
  rule,
  ruleText
}) => {
  if (recurrenceUiType === POS_SCHEDULE_UI_TYPES.ONCE) return 'Una sola vez'
  if (recurrenceUiType === POS_SCHEDULE_UI_TYPES.ONCE_PER_PERIOD) {
    return getOneTimeRecurringDescription(recurrenceInterval)
  }
  if (ruleText) return ruleText
  return rule?.toText?.(SPANISH) || ''
}

export const buildSchedulePayload = ({
  recurrenceType,
  recurrenceInterval,
  rule,
  ruleText
}) => {
  if (recurrenceType === POS_SCHEDULE_UI_TYPES.ONCE) {
    return {
      completion_mode: POS_SCHEDULE_UI_TYPES.ONCE,
      recurrence_format: null,
      recurrence_interval: null,
      recurrence_description: 'Una sola vez'
    }
  }

  if (recurrenceType === POS_SCHEDULE_UI_TYPES.ONCE_PER_PERIOD) {
    const interval = Math.min(Math.max(Number(recurrenceInterval) || 1, 1), 99)
    const weeklyRule = new RRule({ freq: RRule.WEEKLY, interval })

    return {
      completion_mode: POS_SCHEDULE_UI_TYPES.ONCE_PER_PERIOD,
      recurrence_format: weeklyRule.toString(),
      recurrence_interval: interval,
      recurrence_description: getOneTimeRecurringDescription(interval)
    }
  }

  if (recurrenceType === POS_SCHEDULE_UI_TYPES.DAILY) {
    const dailyRule = rule || new RRule({ freq: RRule.DAILY })
    return {
      completion_mode: 'recurring',
      recurrence_format: dailyRule.toString(),
      recurrence_interval: dailyRule.options?.interval || null,
      recurrence_description: getRecurrenceDescription({
        recurrenceUiType: recurrenceType,
        rule: dailyRule,
        ruleText
      })
    }
  }

  if (recurrenceType === POS_SCHEDULE_UI_TYPES.WEEKLY) {
    if (!rule) return null

    return {
      completion_mode: 'recurring',
      recurrence_format: rule.toString(),
      recurrence_interval: rule.options?.interval || null,
      recurrence_description: getRecurrenceDescription({
        recurrenceUiType: recurrenceType,
        recurrenceInterval,
        rule,
        ruleText
      })
    }
  }

  return null
}

export const getScheduleSummaryContent = ({
  recurrenceType,
  recurrenceInterval,
  startDate,
  endDate,
  isUndefinedEndDate,
  ruleText
}) => {
  if (!recurrenceType || !startDate) return null
  if (!isUndefinedEndDate && !endDate) return null

  const startText = dayjs(startDate).format('DD/MM/YYYY')
  const endDateText = endDate ? dayjs(endDate).format('DD/MM/YYYY') : ''
  const weekIntervalLabel = getWeekIntervalLabel(recurrenceInterval)

  if (recurrenceType === POS_SCHEDULE_UI_TYPES.ONCE) {
    if (isUndefinedEndDate) {
      return {
        severity: 'warning',
        parts: [
          { text: 'Esta tarea se creará a partir del ' },
          { text: startText, bold: true },
          {
            text: '. Al completarse una vez en la aplicación, no se le volverá a asignar al usuario ni se mostrará de nuevo.'
          }
        ]
      }
    }

    return {
      severity: 'warning',
      parts: [
        { text: 'Esta tarea debe completarse una sola vez entre el ' },
        { text: startText, bold: true },
        { text: ' y el ' },
        { text: endDateText, bold: true },
        { text: '. Al completarla, o al vencer el plazo, no se le volverá a asignar al usuario.' }
      ]
    }
  }

  if (recurrenceType === POS_SCHEDULE_UI_TYPES.ONCE_PER_PERIOD) {
    if (isUndefinedEndDate) {
      return {
        severity: 'warning',
        parts: [
          { text: 'Esta tarea se creará a partir del ' },
          { text: startText, bold: true },
          { text: ' y se completará una sola vez ' },
          { text: weekIntervalLabel, bold: true },
          { text: ', ocultándose hasta el siguiente periodo, de forma indefinida.' }
        ]
      }
    }

    return {
      severity: 'warning',
      parts: [
        { text: 'Esta tarea debe completarse una sola vez ' },
        { text: weekIntervalLabel, bold: true },
        { text: ', entre el ' },
        { text: startText, bold: true },
        { text: ' y el ' },
        { text: endDateText, bold: true },
        {
          text: '. Al completarla, se ocultará hasta el siguiente periodo en lugar de dejar de asignarse para siempre.'
        }
      ]
    }
  }

  const recurrenceText = ruleText || getRecurrenceDescription({ recurrenceUiType: recurrenceType })

  if (isUndefinedEndDate) {
    return {
      severity: 'info',
      parts: [
        { text: 'Esta tarea se ejecutará ' },
        { text: recurrenceText, bold: true },
        { text: ' a partir del ' },
        { text: startText, bold: true },
        { text: ' de forma ' },
        { text: 'indefinida', bold: true },
        { text: '.' }
      ]
    }
  }

  return {
    severity: 'info',
    parts: [
      { text: 'Esta tarea se ejecutará ' },
      { text: recurrenceText, bold: true },
      { text: ' a partir del ' },
      { text: startText, bold: true },
      { text: ' hasta el ' },
      { text: endDateText, bold: true },
      { text: '.' }
    ]
  }
}

export const getScheduleSummary = (params) => {
  const content = getScheduleSummaryContent(params)
  if (!content) return ''
  return content.parts.map((part) => part.text).join('')
}
